/**
 * RunnerBarContext — the registry behind the Field Service "runner bar".
 *
 * See `types/runnerBar.ts` for the contract this registry carries, and
 * `docs/superpowers/specs/2026-09-09-field-service-workflow-automation-design.md` §7a for
 * the design rule it exists to enforce: **fixed slots, variable contents, always labeled.**
 * The whole point is that the bar renders from the SAME place in the DOM on every Field
 * Service screen — so, exactly like `NavigationContext` guarantees `<Navigation />` renders
 * once per route, this context lets `ModuleLayout` host ONE `<RunnerBar />` per route while
 * individual pages merely declare what should be in it. A page never renders the bar itself
 * and never imports it directly.
 *
 * WHY A REGISTRY AND NOT PROP-DRILLING: `ModuleLayout` sits above the router outlet and has
 * no idea which page is currently mounted, let alone what that page wants in its action bar.
 * The registering page and the rendering bar are on opposite sides of that boundary, so the
 * only way to connect them without `ModuleLayout` importing every Field Service page is a
 * shared piece of state both sides can reach — a page WRITES into it, the bar READS from it.
 *
 * A SUBTLE CORRECTNESS REQUIREMENT LIVES IN `useRunnerBar`'s CLEANUP: it clears its own
 * registration when the calling component unmounts. Without that, navigating away from a
 * ticket detail page to a list page would leave that ticket's identity and primary action on
 * screen — a bar showing a record the user is no longer viewing, with a live button still
 * wired to mutate it. See the effect below for how that is kept independent of `config`
 * updates (which must NOT trigger the same cleanup, or the bar would flash empty on every
 * re-render of a page that legitimately updates its own config, e.g. as `isPending` flips).
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { RunnerBarConfig } from '../types/runnerBar';

interface RunnerBarContextValue {
  readonly config: RunnerBarConfig | null;
  /**
   * The raw `useState` setter, passed through untouched. This is deliberate: React
   * guarantees a `useState` setter is referentially stable for the lifetime of the
   * component instance that owns it, REGARDLESS of whether the object wrapping it (this
   * context value) is memoised. `useRunnerBar` below depends on that guarantee to tell
   * "the provider re-rendered" apart from "the calling page unmounted" — see there for why
   * that distinction is the entire point.
   */
  readonly setConfig: (config: RunnerBarConfig | null) => void;
}

const RunnerBarContext = createContext<RunnerBarContextValue | null>(null);
RunnerBarContext.displayName = 'RunnerBar';

export function RunnerBarProvider({ children }: { children: ReactNode }): ReactElement {
  const [config, setConfig] = useState<RunnerBarConfig | null>(null);

  // Memoised so consumers (RunnerBar, and every page's useRunnerBar) only see a new context
  // value when `config` itself actually changes — not on every render of whatever ancestor
  // re-rendered this provider. Not load-bearing for correctness (React bails out of a
  // `setState` call whose value is `Object.is`-identical to the current one regardless), but
  // it keeps this provider from being a source of pointless re-renders across the whole
  // module, which every one of the 13 modules' routes mounts.
  const value = useMemo<RunnerBarContextValue>(() => ({ config, setConfig }), [config]);

  return <RunnerBarContext.Provider value={value}>{children}</RunnerBarContext.Provider>;
}

/**
 * A page declares the bar's contents. Pass `null` to clear it explicitly — though most pages
 * will reach for `advance: { kind: 'loading' }` instead while data is still arriving, since
 * that keeps zone 1's identity on screen rather than blanking the whole bar.
 *
 * REGISTERS `config` on mount and whenever it changes (a page updating, say, `isPending` on
 * its advance action is expected to pass a new `config` and have the bar reflect it). CLEARS
 * the registration on unmount — this is the requirement called out in the module header, and
 * it is why registration and cleanup are two SEPARATE effects below rather than one effect
 * with a cleanup function:
 *
 *   - The register effect depends on `[setConfig, config]` — it re-runs whenever the page
 *     hands us a new config, which is exactly when the bar should update.
 *   - The cleanup effect depends on `[setConfig]` ONLY. Because `setConfig` is the raw
 *     `useState` setter (stable for the life of the provider instance), this effect's
 *     dependency never changes across re-renders of the calling page — so its cleanup runs
 *     ONLY when the calling component itself unmounts, never merely because `config` changed.
 *     Keying the cleanup to `config` instead (an easy mistake) would clear-then-immediately-
 *     reregister on every legitimate update, which is at best wasted work and at worst a
 *     visible flash of the previous zone-2 state.
 *
 * NEITHER EFFECT CALLS `setConfig` FROM THE RENDER BODY — both run inside `useEffect`, gated
 * by a dependency array. Calling a different component's state setter unconditionally during
 * render (skipping `useEffect` entirely) is the concrete mistake this guards against: React
 * does not allow it, and doing it anyway is how a "just render the bar" hook turns into an
 * update loop. See `RunnerBarContext.test.tsx` for a regression test that renders a
 * registering component many times over and asserts React never logs the
 * "Cannot update a component while rendering a different component" warning that mistake
 * produces.
 *
 * What THIS hook does NOT protect against: a caller that hands it a brand-new `config` object
 * (or a brand-new `advance.onClick`, etc.) on every single render, with no memoisation at
 * all. That IS an infinite loop, but it is the caller's bug, not this hook's — the moment a
 * config's identity is expected to change (loading -> action, isPending true -> false) is
 * legitimate and must propagate, so nothing in this file can distinguish "the data really did
 * change" from "the caller forgot to memoise" by inspecting reference equality alone. Pages
 * are responsible for keeping their `RunnerBarConfig` referentially stable across renders that
 * do not represent an actual change (`useMemo`, or a config built from otherwise-stable
 * values).
 *
 * A NO-OP OUTSIDE `RunnerBarProvider` — `useContext` returns `null` there, so `setConfig` is
 * `undefined` and both effects become no-ops. The bar is Field Service only; a page rendered
 * in an isolated test harness, a Storybook story, or (by mistake) another module must still
 * render normally rather than crash for want of a provider.
 *
 * TWO PAGES REGISTERING AT ONCE (e.g. briefly, during a route transition where the outgoing
 * and incoming page are both mounted) is a bug in the caller, not a case this hook reconciles.
 * Last write wins.
 */
export function useRunnerBar(config: RunnerBarConfig | null): void {
  const ctx = useContext(RunnerBarContext);
  const setConfig = ctx?.setConfig;

  useEffect(() => {
    setConfig?.(config);
  }, [setConfig, config]);

  useEffect(() => {
    return () => {
      setConfig?.(null);
    };
  }, [setConfig]);
}

/** The bar itself reads this. `null` when no page has registered. */
export function useRunnerBarConfig(): RunnerBarConfig | null {
  return useContext(RunnerBarContext)?.config ?? null;
}
